import React, { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronDown, History, Info, RefreshCw } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Note } from '@/components/app/Badges';
import { getReferences, getReferencesSnapshot, hiddenInactiveCount, readReferencesState, refOptions, subscribeReferences, EVENT_STATUS_LABELS, OPERATIONAL_LABELS, PRIORITY_LABELS, SERVICE_STATUS_LABELS, TIME_STATUS_LABELS } from '@/lib/driftApi';
import { cn } from '@/lib/utils';

/* Felles byggeklosser for registreringssidene (timer, vareforbruk, maskinpark). */

/**
 * Diskret, tydelig eksempeldata-status. Ingen påstand om kobling til SmartDok eller andre systemer.
 * `details`: lengre forklaring som ligger bak «Om eksempeldata» – den korte setningen er alltid synlig.
 */
export function ExampleDataNote({ children, details, className }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={cn('flex items-start gap-2 rounded-[10px] border border-[var(--hr-hairline)] bg-[var(--hr-surface-2)] px-3 py-2.5 text-sm leading-5 text-[var(--hr-muted)]', className)} data-testid="example-data-note">
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <span>
          <span className="font-semibold text-[var(--hr-text)]">Eksempeldata.</span> Fiktive medarbeidere, kunder, prosjekter og registreringer. Ingen kobling til SmartDok eller andre systemer.{children ? <> {children}</> : null}
        </span>
        {details && (
          <>
            {' '}
            <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="inline-flex items-center gap-0.5 rounded-sm font-medium text-[var(--hr-text)] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid="example-data-details-toggle">
              Om eksempeldata
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform duration-150', open && 'rotate-180')} aria-hidden="true" />
            </button>
            {open && (
              <p className="mt-1.5 border-t border-[var(--hr-hairline)] pt-1.5 text-[13px] leading-5 text-[var(--hr-muted)]" data-testid="example-data-details">
                {details}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Referansedata deles mellom sidene. Henter automatisk på nytt ved feil; `retry` tvinger nytt forsøk nå. */
export function useDriftRefs() {
  useSyncExternalStore(subscribeReferences, getReferencesSnapshot, getReferencesSnapshot);
  const { refs, error, loading, attempts } = readReferencesState();
  const retry = useCallback(() => getReferences({ force: true }).catch(() => {}), []);
  return { refs, error, loading, attempts, retry };
}

/** Feil ved henting av referansedata – med «Prøv igjen». Brukes i oversikt og i skjema. */
export function RefsErrorNote({ error, loading, onRetry, what = 'prosjekter og medarbeidere', className, testId = 'refs-error' }) {
  const transient = error?.status === undefined || [502, 503, 504].includes(error?.status);
  return (
    <Note tone="amber" className={className} data-testid={testId} role="alert">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <span>
          Kunne ikke hente {what}.{' '}
          {transient ? 'Tjenesten kan være i ferd med å starte – vi prøver igjen automatisk.' : error?.message ?? ''}
        </span>
        <Button variant="outline" size="sm" className="h-9 shrink-0 rounded-[8px] border-[rgba(154,91,0,0.35)] bg-white/70 text-sm text-[#5C3700] hover:bg-white" onClick={onRetry} disabled={loading} data-testid={`${testId}-retry`}>
          <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', loading && 'animate-spin')} aria-hidden="true" />
          {loading ? 'Henter …' : 'Prøv igjen'}
        </Button>
      </div>
    </Note>
  );
}

/** Lasteskjelett for skjema – med synlig tekst, så det aldri ser «hengt» ut. */
export function FormLoading({ text = 'Henter prosjekter og medarbeidere …', rows = 5, testId = 'form-loading' }) {
  return (
    <div className="space-y-4" aria-busy="true" aria-live="polite" data-testid={testId}>
      <div className="flex items-center gap-2 text-[14px] text-[var(--hr-muted)]">
        <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        <span>{text}</span>
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="space-y-1.5">
          <Skeleton className="h-3 w-28" />
          <Skeleton className="h-10 w-full rounded-[10px]" />
        </div>
      ))}
    </div>
  );
}

/** Skjemainnhold som avhenger av referansedata: skjema / feil med «Prøv igjen» / lasting. */
export function RefsGate({ refs, error, loading, onRetry, what, loadingText, children }) {
  if (refs) return children;
  if (error && !loading) return <RefsErrorNote error={error} loading={loading} onRetry={onRetry} what={what} testId="form-refs-error" />;
  return <FormLoading text={loadingText} />;
}

/** Kompakt status under et enkelt felt som trenger referansedata (f.eks. medarbeiderliste). */
export function RefsInlineStatus({ refs, error, loading, onRetry, what = 'medarbeiderlisten', testId = 'refs-inline' }) {
  if (refs) return null;
  if (loading || !error) {
    return (
      <p className="flex items-center gap-1.5 text-[13px] text-[var(--hr-muted-2)]" aria-live="polite" data-testid={`${testId}-loading`}>
        <RefreshCw className="h-3 w-3 animate-spin" aria-hidden="true" /> Henter {what} …
      </p>
    );
  }
  return (
    <p className="text-[13px] text-[#5C3700]" role="alert" data-testid={`${testId}-error`}>
      Kunne ikke hente {what}.{' '}
      <button type="button" onClick={onRetry} className="font-medium underline underline-offset-2 hover:text-[var(--hr-heading)]" data-testid={`${testId}-retry`}>
        Prøv igjen
      </button>
    </p>
  );
}

export function useIsDesktop(query = '(min-width: 1024px)') {
  const [match, setMatch] = useState(() => (typeof window !== 'undefined' ? window.matchMedia(query).matches : true));
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return match;
}

export function Field({ label, htmlFor, hint, error, required, children, className }) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={htmlFor} className="text-sm font-medium leading-5 text-[var(--hr-text)]">
        {label}
        {required && <span className="text-[var(--hr-red)]"> *</span>}
      </Label>
      {children}
      {error ? <p className="text-[13px] text-[var(--hr-red)]" role="alert">{error}</p> : hint ? <p className="text-[13px] text-[var(--hr-muted-2)]">{hint}</p> : null}
    </div>
  );
}

export const inputClass = 'h-11 rounded-[10px] border-[var(--hr-border)] bg-white text-base shadow-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)] lg:h-10 lg:text-[15px]';

/** Nedtrekk med store berøringsflater. options: [{ value, label, hint }] */
export function SelectField({ id, value, onValueChange, options, placeholder = 'Velg', disabled, allowEmpty, emptyLabel = 'Ingen', testId, className }) {
  return (
    <Select value={value ?? ''} onValueChange={(v) => onValueChange(v === '__none' ? '' : v)} disabled={disabled}>
      <SelectTrigger id={id} className={cn(inputClass, 'min-w-0 justify-between gap-2 [&>span]:min-w-0 [&>span]:truncate [&>svg]:shrink-0', className)} data-testid={testId}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="rounded-[12px]">
        {allowEmpty && (
          <SelectItem value="__none" className="rounded-[8px] text-[var(--hr-muted)]">
            {emptyLabel}
          </SelectItem>
        )}
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} disabled={!!o.disabled} className="rounded-[8px]" data-testid={testId ? `${testId}-option-${o.value}` : undefined}>
            {o.label}
            {o.hint && <span className="ml-1.5 text-[13px] text-[var(--hr-muted-2)]">{o.hint}</span>}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Nedtrekk fra et register (medarbeidere, prosjekter, varer …): viser kun aktive poster + verdien som allerede står på posten.
 * Finnes arkiverte/avsluttede poster, kan de hentes fram med «Vis arkiverte» – de merkes tydelig og kan ikke velges for nye
 * registreringer (backenden avviser dem også), men den som alt står på posten beholdes.
 */
export function RefSelect({ list, value, onValueChange, idKey = 'id', getLabel, getHint, testId, className, ...rest }) {
  const [showAll, setShowAll] = useState(false);
  const hidden = hiddenInactiveCount(list, value, idKey);
  const options = refOptions(list, { value, includeInactive: showAll, idKey, label: getLabel, hint: getHint }).map((o) => (o.inactive && o.value !== value ? { ...o, disabled: true, hint: `${o.hint} – kan ikke velges` } : o));
  return (
    <div className={cn('flex min-w-0 flex-col gap-1', className)}>
      <SelectField value={value} onValueChange={onValueChange} options={options} testId={testId} {...rest} />
      {hidden > 0 && (
        <button type="button" onClick={() => setShowAll((s) => !s)} className="self-start rounded-sm text-[12px] text-[var(--hr-muted)] underline-offset-2 hover:text-[var(--hr-heading)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid={testId ? `${testId}-toggle-archived` : undefined}>
          {showAll ? 'Skjul arkiverte og avsluttede' : `Vis arkiverte og avsluttede (${hidden})`}
        </button>
      )}
    </div>
  );
}

const TONE = {
  neutral: 'border-[var(--hr-hairline)] bg-[var(--hr-surface-2)] text-[var(--hr-text)]',
  green: 'border-[rgba(6,118,71,0.25)] bg-[var(--hr-green-bg)] text-[var(--hr-green)]',
  amber: 'border-[rgba(154,91,0,0.25)] bg-[var(--hr-amber-bg)] text-[var(--hr-amber)]',
  red: 'border-[rgba(180,35,24,0.25)] bg-[var(--hr-red-bg)] text-[var(--hr-red)]',
  navy: 'border-[rgba(29,40,86,0.2)] bg-[rgba(29,40,86,0.06)] text-[var(--hr-navy)]',
};

export function StatusPill({ tone = 'neutral', children, className, ...props }) {
  return (
    <span className={cn('inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-[13px] font-medium leading-none', TONE[tone], className)} {...props}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
      {children}
    </span>
  );
}

/** Timestatus: utkast nøytral, sendt navy, returnert amber (krever handling), godkjent grønn. */
export const TIME_STATUS_TONE = { utkast: 'neutral', sendt: 'navy', returnert: 'amber', godkjent: 'green' };

export function TimeStatusPill({ status, ...props }) {
  return (
    <StatusPill tone={TIME_STATUS_TONE[status] ?? 'neutral'} {...props}>
      {TIME_STATUS_LABELS[status] ?? status}
    </StatusPill>
  );
}

/** Sakstatus. Avsluttet (løst/utført) er grønn, under arbeid navy, nye saker nøytrale – aldri grønt før saken faktisk er avsluttet. */
export function EventStatusPill({ status, ...props }) {
  const tone = status === 'utfort' || status === 'lost' ? 'green' : status === 'under_utbedring' || status === 'mottatt' ? 'navy' : 'neutral';
  return (
    <StatusPill tone={tone} {...props}>
      {EVENT_STATUS_LABELS[status] ?? status}
    </StatusPill>
  );
}

/** Prioritet på feilmeldinger: kritisk rød, høy amber, ellers nøytral. */
export function PriorityPill({ priority, className, ...props }) {
  const tone = priority === 'kritisk' ? 'red' : priority === 'hoy' ? 'amber' : 'neutral';
  return (
    <StatusPill tone={tone} className={className} {...props}>
      {PRIORITY_LABELS[priority] ?? priority}
    </StatusPill>
  );
}

/** Driftsstatus for maskinen: kan ikke brukes (rød) → avventer frigivelse (amber) → i drift (grønn). */
export function OperationalPill({ status, label, className, ...props }) {
  const tone = status === 'stopp' ? 'red' : status === 'avklaring' ? 'amber' : 'green';
  return (
    <StatusPill tone={tone} className={className} {...props}>
      {label ?? OPERATIONAL_LABELS[status] ?? status}
    </StatusPill>
  );
}

export function ServiceStatusPill({ status, ...props }) {
  const tone = status === 'ok' ? 'green' : status === 'snart' ? 'amber' : status === 'forfalt' ? 'red' : 'neutral';
  return (
    <StatusPill tone={tone} {...props}>
      {SERVICE_STATUS_LABELS[status] ?? status}
    </StatusPill>
  );
}

/** Registreringspanel: høyre side på desktop, bunnark på mobil/nettbrett. Lagre-knapp ligger alltid synlig nederst. */
export function FormSheet({ open, onOpenChange, title, description, children, footer, testId }) {
  const desktop = useIsDesktop();
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={desktop ? 'right' : 'bottom'} className={cn('flex flex-col gap-0 p-0', desktop ? 'w-[520px] sm:max-w-[520px]' : 'h-[92dvh] rounded-t-[18px]')} data-testid={testId}>
        <SheetHeader className="shrink-0 border-b border-[var(--hr-hairline)] px-5 py-4 text-left">
          <SheetTitle className="font-display text-[18px] font-semibold leading-6 text-[var(--hr-heading)]">{title}</SheetTitle>
          {description && <SheetDescription className="text-sm leading-5 text-[var(--hr-muted)]">{description}</SheetDescription>}
        </SheetHeader>
        <div className="hr-scroll min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="shrink-0 border-t border-[var(--hr-hairline)] bg-white px-5 py-3 [padding-bottom:max(12px,env(safe-area-inset-bottom))]">{footer}</div>}
      </SheetContent>
    </Sheet>
  );
}

/** Små nøkkeltall øverst på registreringssidene. Lange tekstverdier (f.eks. «528 meter · 15 stk») settes mindre. */
export function MiniStat({ label, value, meta, tone, testId }) {
  const long = typeof value === 'string' && value.length > 12;
  return (
    <div className="min-w-0 px-4 py-3 sm:px-5" data-testid={testId}>
      <div className="text-[13px] text-[var(--hr-muted)]">{label}</div>
      <div className={cn('hr-num mt-0.5 font-semibold tracking-tight text-[var(--hr-heading)]', long ? 'text-[16px] leading-6' : 'text-[20px] leading-7')}>{value}</div>
      {meta && <div className={cn('truncate text-[13px]', tone === 'amber' ? 'text-[var(--hr-amber)]' : 'text-[var(--hr-muted-2)]')}>{meta}</div>}
    </div>
  );
}

/* ---------- Endringshistorikk (eksempeldata) ---------- */

const HISTORY_KIND = { endret: 'Endret', status: 'Status', opprettet: 'Opprettet', sendt: 'Sendt inn', trukket: 'Trukket tilbake', returnert: 'Returnert', godkjent: 'Godkjent', notat: 'Notat', arbeid: 'Utført arbeid', lost: 'Løst', utfort: 'Utført', frigitt: 'Frigitt', gjenapnet: 'Gjenåpnet', vedlegg: 'Vedlegg lagt til', vedlegg_fjernet: 'Vedlegg fjernet', service_utfort: 'Service utført', arkivert: 'Arkivert', reaktivert: 'Reaktivert', avsluttet: 'Avsluttet', underprosjekt: 'Underprosjekt' };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function historyValue(v) {
  if (v === null || v === undefined || v === '') return <span className="text-[var(--hr-muted-2)]">tom</span>;
  if (typeof v === 'number') return new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 2 }).format(v);
  // ISO-datoer (f.eks. «Sist service (dato)») vises som dd.mm.åååå, slik som ellers i portalen.
  if (typeof v === 'string' && ISO_DATE.test(v)) return v.split('-').reverse().join('.');
  return String(v);
}

/**
 * Historikk for en registrering: tidspunkt, aktør («Kontor (eksempeldata)»), årsak og feltvis før → etter.
 * Ærlig eksempelmarkering – ikke en autentisert revisjonslogg.
 */
export function HistoryList({ history, className, testId = 'history' }) {
  if (!history?.length) {
    return (
      <p className={cn('text-[14px] text-[var(--hr-muted)]', className)} data-testid={`${testId}-empty`}>
        Ingen endringer registrert.
      </p>
    );
  }
  const items = [...history].reverse();
  return (
    <ol className={cn('space-y-3', className)} data-testid={testId}>
      {items.map((h, i) => (
        <li key={`${h.at}-${i}`} className="rounded-[10px] border border-[var(--hr-hairline)] bg-[var(--hr-surface-2)] px-3 py-2.5" data-testid={`${testId}-item`}>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
            <span className="font-semibold text-[var(--hr-heading)]">{HISTORY_KIND[h.kind] ?? h.kind}</span>
            <span className="hr-tabular text-[var(--hr-muted)]">{formatHistoryTime(h.at)}</span>
            <span className="text-[var(--hr-muted-2)]">·</span>
            <span className="text-[var(--hr-muted)]" data-testid={`${testId}-by`}>{h.by}</span>
          </div>
          {h.reason && <p className="mt-1 text-[13px] italic text-[var(--hr-text)]">«{h.reason}»</p>}
          {h.note && <p className="mt-1 text-[13px] leading-5 text-[var(--hr-text)]">{h.note}</p>}
          {h.changes?.length > 0 && (
            <dl className="mt-2 grid gap-1 text-[13px]">
              {h.changes.map((c) => (
                <div key={c.field} className="grid grid-cols-[minmax(0,110px)_minmax(0,1fr)] gap-2 sm:grid-cols-[minmax(0,130px)_minmax(0,1fr)]" data-testid={`${testId}-change`}>
                  <dt className="truncate text-[var(--hr-muted)]" title={c.label}>{c.label}</dt>
                  <dd className="hr-tabular min-w-0 break-words text-[var(--hr-text)]">
                    <span className="line-through decoration-[var(--hr-muted-2)]">{historyValue(c.from)}</span>
                    <span className="mx-1.5 text-[var(--hr-muted-2)]">→</span>
                    <span className="font-medium text-[var(--hr-heading)]">{historyValue(c.to)}</span>
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </li>
      ))}
    </ol>
  );
}

export function formatHistoryTime(iso) {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('nb-NO', { timeZone: 'Europe/Oslo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/** Diskret «Rettet»-merke på registreringer med endringshistorikk. */
export function EditedMark({ count, className, testId }) {
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full border border-[var(--hr-hairline)] bg-white px-1.5 py-px text-[12px] font-medium text-[var(--hr-muted)]', className)} title="Registreringen er rettet – se historikk" data-testid={testId}>
      <History className="h-3 w-3" aria-hidden="true" />
      Rettet{count > 1 ? ` ×${count}` : ''}
    </span>
  );
}

/** Seksjonsoverskrift inne i et skjema/ark. */
export function SheetSection({ title, description, children, className }) {
  return (
    <section className={cn('border-t border-[var(--hr-hairline)] pt-4', className)}>
      <h3 className="text-[15px] font-semibold leading-5 text-[var(--hr-heading)]">{title}</h3>
      {description && <p className="mt-0.5 text-[13px] leading-[18px] text-[var(--hr-muted)]">{description}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

/** Nøkkeltall som lenker til registreringene bak tallet. `linkLabel` kan overstyres der tallet ikke er registreringer (f.eks. avvik). */
export function LinkStat({ label, value, meta, to, testId, tone, linkLabel = 'Åpne registreringer' }) {
  const long = typeof value === 'string' && value.length > 12;
  const inner = (
    <>
      <div className="text-[13px] text-[var(--hr-muted)]">{label}</div>
      <div className={cn('hr-num mt-0.5 font-semibold tracking-tight text-[var(--hr-heading)]', long ? 'text-[16px] leading-6' : 'text-[20px] leading-7')}>{value}</div>
      {meta && <div className={cn('truncate text-[13px]', tone === 'amber' ? 'text-[var(--hr-amber)]' : 'text-[var(--hr-muted-2)]')}>{meta}</div>}
    </>
  );
  if (!to) {
    return (
      <div className="min-w-0 px-4 py-3 sm:px-5" data-testid={testId}>
        {inner}
      </div>
    );
  }
  return (
    <Link to={to} className="group/stat block min-w-0 px-4 py-3 transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)] sm:px-5" data-testid={testId} aria-label={`${label}: ${linkLabel.toLowerCase()}`}>
      {inner}
      <div className="mt-0.5 flex items-center gap-1 text-[13px] font-medium text-[var(--hr-accent-text)] opacity-0 transition-opacity duration-150 group-hover/stat:opacity-100 group-focus-visible/stat:opacity-100">
        {linkLabel} <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
      </div>
    </Link>
  );
}
